import { createAdminDepartureCoordinator } from "./adminDepartureCoordinator";
import { createAdminDepartureHistory } from "./adminDepartureHistory";

/** Shared by the router and by each mounted, independently dirty editor. */
export const adminDepartureCoordinator = createAdminDepartureCoordinator();

let history: ReturnType<typeof createAdminDepartureHistory> | undefined;

/** One pre-router POP controller shared with the navigator adapter. */
export function getAdminDepartureHistory() {
  return (history ??= createAdminDepartureHistory(adminDepartureCoordinator));
}

let approvedExitDepth = 0;

/** Permit the synchronous navigation inside a fully approved exit callback. */
export function runApprovedAdminExit<T>(exit: () => T): T {
  approvedExitDepth += 1;
  try {
    return exit();
  } finally {
    approvedExitDepth -= 1;
  }
}

export function isApprovedAdminExit(): boolean {
  return approvedExitDepth > 0;
}

export interface ApprovedAdminExit {
  /** One synchronous commit, still bound to the owners and source that consented. */
  run(action: () => void): boolean;
}

/** Keep consent scoped to one continuation; never extend the router bypass across await. */
export function captureApprovedAdminExit(): ApprovedAdminExit {
  const destination = { kind: "exit" } as const;
  const ownersAreCurrent =
    adminDepartureCoordinator.captureValidity(destination);
  const requestIsCurrent = adminDepartureCoordinator.captureRequestValidity();
  const sourceUrl = window.location.href;
  let consumed = false;
  return {
    run(action) {
      if (consumed) return false;
      consumed = true;
      if (
        window.location.href !== sourceUrl ||
        !requestIsCurrent() ||
        (!ownersAreCurrent() &&
          adminDepartureCoordinator.needsInterception(destination))
      )
        return false;
      runApprovedAdminExit(action);
      return true;
    },
  };
}
